import { yupResolver } from '@hookform/resolvers/yup'
import {
  EXECUTIVE_OFFICER_AS_EMPLOYEE,
  EXECUTIVE_OFFICER_SELF_EMPLOYED,
  type ContractEndReasons,
  type ContractType,
  type TemporaryContractReasonType,
  type TutorFieldSchema
} from '@libs/data-access/entities'
import { membershipService, modulationService } from '@libs/data-access/queries'
import { phoneInputUtils, type PhoneInputValue } from '@libs/ui/ds'
import { useCommonAppTranslation } from '@libs/utils/translations'
import { useTrackMemberCreatedInteraction } from '@spa/core/externals/segment/methods/track/hooks/useTrackMemberCreatedInteraction'
import { useMemoSelector } from '@spa/redux/hooks'
import moment from 'moment'
import { type MutableRefObject, useEffect, useState } from 'react'
import { type FieldError, useForm, useWatch } from 'react-hook-form'
import { useAccountPermissions } from 'snap-redux/modules/accounts/hooks'
import { getCurrentAccount } from 'snap-redux/modules/accounts/selectors'
import { CDD, CDI } from 'snap-redux/modules/contracts'
import { getFirstLocationByAccountId } from 'snap-redux/modules/locations/selectors'
import { createMembership } from 'snap-redux/modules/memberships'
import { getTeamsByLocationId } from 'snap-redux/modules/teams/selectors'
import { getCurrentUser } from 'snap-redux/modules/users/selectors'
import { useActions } from 'snap-redux/utils'

import { useNewUserFormValidation } from './useNewUserFormValidation'

type Props = {
  teamId?: number
  accountId?: number | string
  locationId?: number
  formSuccess?: (value?: any) => void
  formRef?: MutableRefObject<any>
}

export type NewUserFormType = TutorFieldSchema & {
  location_id: number
  team_id: number
  birth_date: string
  contract_start: string
  contract_hour: string
  firstname: string
  lastname: string
  birth_name: string
  employee_id: string
  payroll_provider_sync: boolean
  mobile_phone: PhoneInputValue
  sms_notification_enabled: boolean
  fixed_phone: PhoneInputValue
  email: string
  contract_type: ContractType
  temporary_contract_reason: TemporaryContractReasonType
  contract_time: number
  contract_end: string
  contract_end_reason: ContractEndReasons
  has_modulation_period: boolean
  temp_agency_address: string
  temp_agency_name: string
  temp_agency_zip: string
  temp_agency_city: string
  line_manager_id: string | null
}

const actionCreators = {
  createMembership
}

const useNewUserForm = ({
  teamId,
  accountId,
  locationId,
  formSuccess,
  formRef
}: Props) => {
  const { t } = useCommonAppTranslation()
  const { sms_enabled } = useAccountPermissions()
  const { trackCreation } = useTrackMemberCreatedInteraction()

  const currentUser = useMemoSelector(getCurrentUser)
  const actions = useActions(actionCreators)
  const account = useMemoSelector(getCurrentAccount)
  const [formLoading, setFormLoading] = useState(false)
  const [formError, setFormError] = useState(null)

  const defaultStaticValues = {
    contract_hour: '08:00',
    firstname: '',
    lastname: '',
    birth_name: '',
    birth_date: null,
    employee_id: '',
    payroll_provider_sync: false,
    mobile_phone: {
      countryCode: account?.country,
      number: ''
    },
    sms_notification_enabled: false,
    fixed_phone: {
      countryCode: account?.country,
      number: ''
    },
    email: '',
    contract_type: null,
    temporary_contract_reason: null,
    contract_time: null,
    contract_end: '',
    has_modulation_period: false,
    tutor_membership_id: null,
    temp_agency_name: null,
    temp_agency_address: null,
    temp_agency_zip: null,
    temp_agency_city: null
  }

  const formValidation = useNewUserFormValidation({ country: account?.country })

  const firstLocation = useMemoSelector(state =>
    getFirstLocationByAccountId(state, accountId)
  )
  const currentLocationId = locationId || firstLocation?.get('id')

  const teams = useMemoSelector(state =>
    getTeamsByLocationId(state, currentLocationId)
  )

  const defaultTeam = teamId || teams?.first().get('id')

  const methods = useForm<NewUserFormType>({
    defaultValues: {
      ...defaultStaticValues,
      // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
      location_id: currentLocationId || null,
      // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
      team_id: defaultTeam || null,
      contract_start: moment().format('YYYY-MM-DD')
    },
    mode: 'onBlur',
    resolver: yupResolver(formValidation)
  })

  const {
    control,
    formState,
    setValue,
    setError,
    clearErrors,
    getValues,
    resetField
  } = methods

  const haveSmsNotif = useWatch({ control, name: 'sms_notification_enabled' })

  const isPhoneEmpty = phoneInputUtils.isValid(
    useWatch({ control, name: 'mobile_phone' })
  )
  const isFieldMobilePhoneError = Boolean(
    (formState.errors?.mobile_phone as FieldError)?.message
  )

  useEffect(() => {
    if (!sms_enabled) {
      return
    }
    if (isFieldMobilePhoneError) {
      setError('sms_notification_enabled', {
        type: 'focus',
        message: t('userInfo.smsNotificationError')
      })
    } else if (isPhoneEmpty && haveSmsNotif === true) {
      setError('sms_notification_enabled', {
        type: 'focus',
        message: t('userInfo.smsNotificationError')
      })
    } else {
      clearErrors('sms_notification_enabled')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFieldMobilePhoneError, isPhoneEmpty, setError, haveSmsNotif])
  const modulationMutation =
    modulationService.modulationUserContracts.useCreateOne()

  const onSubmit = (formData: NewUserFormType) => {
    setFormLoading(true)

    const isExecutiveOfficer =
      formData?.contract_type === EXECUTIVE_OFFICER_SELF_EMPLOYED ||
      formData?.contract_type === EXECUTIVE_OFFICER_AS_EMPLOYEE

    // Split the time in hours and minutes
    const hoursMinutes = formData?.contract_hour?.split(':') || []

    const startTime = moment(formData?.contract_start)
      .clone()
      .hours(Number(hoursMinutes[0]) || Number('08'))
      .minutes(Number(hoursMinutes[1]) || Number('00'))

    const addContractEndReason =
      formData?.contract_type?.toUpperCase() !== CDI &&
      Boolean(formData?.contract_type) &&
      Boolean(formData?.contract_end)

    const isSpain = account?.country === 'ES'

    const newUserData = {
      account_id: accountId,
      firstname: formData?.firstname,
      lastname: formData?.lastname,
      birth_name: formData?.birth_name,
      birth_date: formData?.birth_date,
      mobile_phone_attributes: formData?.mobile_phone?.number
        ? {
            number: phoneInputUtils.transformToInternationalFormat(
              formData?.mobile_phone
            )
          }
        : null,
      fixed_line_phone_attributes: formData?.fixed_phone?.number
        ? {
            number: phoneInputUtils.transformToInternationalFormat(
              formData?.fixed_phone
            )
          }
        : null,
      sms_notification_enabled: formData?.sms_notification_enabled,
      locale: currentUser?.locale,
      user_attributes: {
        email: formData?.email
      },
      ...(formData.employee_id.length && { employee_id: formData.employee_id }),
      payroll_provider_sync: formData.payroll_provider_sync
        ? moment().format('YYYY-MM-DD')
        : null,
      user_contract_attributes: {
        contract_start: moment(formData?.contract_start).format('YYYY-MM-DD'),
        contract_type: formData?.contract_type,
        ...(formData?.contract_type?.toUpperCase() === CDD && {
          temporary_contract_reason: formData?.temporary_contract_reason
        }),
        ...(!isExecutiveOfficer && {
          contract_time: formData?.contract_time
        }),
        ...(!isExecutiveOfficer && {
          contract_end:
            (formData?.contract_end &&
              moment(formData?.contract_end).format('YYYY-MM-DD')) ||
            null
        }),
        ...(addContractEndReason && {
          contract_end_reason: isSpain
            ? 'resignation_or_termination'
            : 'termination'
        }),
        team_id: formData?.team_id,
        location_id: formData?.location_id,
        ...(!isExecutiveOfficer && { start_time: startTime }),
        tutor_membership_id: formData?.tutor_membership_id,
        temp_agency_address: formData?.temp_agency_address,
        temp_agency_name: formData?.temp_agency_name,
        temp_agency_zip: formData?.temp_agency_zip,
        temp_agency_city: formData?.temp_agency_city,
        ...(!isExecutiveOfficer && {
          line_manager_id: formData?.line_manager_id
        })
      }
    }

    actions.createMembership(newUserData).then(
      data => {
        trackCreation()
        // create modulation user contract from here to get the contract id
        if (formData.has_modulation_period) {
          modulationMutation.mutate({
            payload: { user_contract_id: data?.contracts[0].id }
          })
        }

        if (formSuccess) {
          formSuccess(data)

          if (formData?.line_manager_id) {
            membershipService.membership.invalidateOne(
              Number(formData.line_manager_id)
            )
          }
        }
      },
      error => {
        formRef?.current?.scrollIntoView({ behavior: 'smooth' })
        setFormLoading(false)
        setFormError(error)
      }
    )
  }

  return {
    control,
    getValues,
    setValue,
    setError,
    clearErrors,
    formLoading,
    formError,
    onSubmit,
    isFieldMobilePhoneError,
    methods,
    formState,
    resetField
  }
}

export default useNewUserForm
